import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import type { ProjectSearch } from '../types/question.types';
import { toProjectFilterSearch, toProjectFilterValues } from '../utils/questionFilters';

const route = getRouteApi('/_auth/question/projects');

const normalizeFilterValues = (values: Record<string, string>) =>
  toProjectFilterValues(toProjectFilterSearch(values));

export function useQuestionProjectFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toProjectFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toProjectFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const nameSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const listSearch = useMemo<ProjectSearch>(
    () => ({ search: search.search, classId: search.classId, isActive: search.isActive }),
    [search.search, search.classId, search.isActive],
  );

  return { ...filters, ...nameSearch, search: listSearch };
}
